(function (global) {
  'use strict';

  function createEngine({ mount, best = 0, onUpdate = () => {}, onGameOver = () => {}, config = {}, assets = {} }) {
    const Phaser = global.Phaser;
    const rules = global.BBC_DIAPER_DASH_RULES;
    if (!Phaser || !rules || !mount) throw new Error('Diaper Dash could not load its game engine.');
    const model = rules.createGame({ best, config: { emitterOffsetX: 43, ...config } });
    const dropViews = new Map();
    const effects = new Set();
    const reducedMotion = global.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
    let sceneRef = null;
    let lastHudSignature = '';

    function drawBaby(scene) {
      if (scene.textures.exists('dash-baby')) {
        const art = scene.add.image(0, 0, 'dash-baby').setDisplaySize(300, 200);
        const container = scene.add.container(0, 0, [art]);
        container.art = art;
        if (!reducedMotion) scene.tweens.add({ targets: art, y: { from: -3, to: 3 }, angle: { from: -1.5, to: 1.5 }, duration: 1250, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
        return container;
      }
      const drawing = scene.add.graphics();
      drawing.fillStyle(0xf3c6ab).fillCircle(0, -25, 44);
      drawing.fillStyle(0x6e5546).fillEllipse(0, -53, 77, 24);
      drawing.fillStyle(0xf1d8b5).fillEllipse(0, 30, 120, 110);
      drawing.fillStyle(0xffffff).fillRoundedRect(16, 36, 92, 54, 18);
      const label = scene.add.text(0, -93, 'Baby', { fontFamily: 'Arial', fontSize: '22px', color: '#44362f' }).setOrigin(0.5);
      return scene.add.container(0, 0, [drawing, label]);
    }

    function drawCatcher(scene) {
      if (scene.textures.exists('dash-diaper')) {
        const frame = scene.textures.getFrame('dash-diaper');
        const visualHeight = 214;
        const visualWidth = 320 * 0.7;
        const cropWidth = Math.min(frame.width, visualWidth * frame.height / visualHeight);
        const art = scene.add.image(0, 0, 'dash-diaper')
          .setCrop((frame.width - cropWidth) / 2, 0, cropWidth, frame.height)
          .setScale(visualWidth / frame.width, visualHeight / frame.height);
        const container = scene.add.container(0, model.config.catcherY, [art]);
        container.art = art;
        return container;
      }
      const drawing = scene.add.graphics();
      const width = model.config.catcherWidth;
      const height = model.config.catcherHeight;
      drawing.fillStyle(0xffffff).fillRoundedRect(-width / 2, -height / 2, width, height, 34);
      drawing.lineStyle(4, 0x315e4e).strokeRoundedRect(-width / 2, -height / 2, width, height, 34);
      return scene.add.container(0, model.config.catcherY, [drawing]);
    }

    function drawDrop(scene, drop) {
      const key = drop.type === 'pee' ? 'dash-pee' : 'dash-poop';
      let art;
      if (scene.textures.exists(key)) {
        art = scene.add.image(0, 0, key);
        art.setDisplaySize(drop.type === 'pee' ? 42 : 49, drop.type === 'pee' ? 45 : 43);
      } else {
        art = scene.add.graphics();
        art.fillStyle(drop.type === 'pee' ? 0xffd953 : 0xa16a4a).fillEllipse(0, 0, 38, 42);
      }
      const container = scene.add.container(drop.x, drop.y, [art]);
      if (!reducedMotion) scene.tweens.add({ targets: art, angle: { from: drop.type === 'pee' ? -5 : -9, to: drop.type === 'pee' ? 5 : 9 }, duration: drop.type === 'pee' ? 300 : 480, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
      return container;
    }

    function sparkle(scene, x, y, color, radius, dx, dy, duration) {
      const dot = scene.add.circle(x, y, radius, color, 0.9).setDepth(10);
      effects.add(dot);
      scene.tweens.add({ targets: dot, x: x + dx, y: y + dy, alpha: 0, scale: 0.3, duration, ease: 'Quad.Out', onComplete: () => { effects.delete(dot); dot.destroy(); } });
    }

    function releaseEffect(scene, drop) {
      if (reducedMotion) return;
      if (scene.textures.exists('dash-baby-playful') && scene.babyView.art) {
        scene.babyView.art.setTexture('dash-baby-playful');
        scene.expressionUntil = scene.time.now + 430;
      }
      const color = drop.type === 'pee' ? 0xffe15f : 0xc18654;
      for (let index = 0; index < 4; index++) sparkle(scene, drop.x, drop.y, color, 3 + index % 2, (index - 1.5) * 10, 12 + index * 5, 210);
      if (scene.babyView.art) {
        scene.tweens.killTweensOf(scene.babyView.art);
        scene.tweens.add({ targets: scene.babyView.art, scaleX: 1.05, scaleY: 0.96, duration: 100, yoyo: true, onComplete: () => {
          if (scene.babyView.art?.active) scene.tweens.add({ targets: scene.babyView.art, y: { from: -3, to: 3 }, angle: { from: -1.5, to: 1.5 }, duration: 1250, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
        } });
      }
    }

    function impactEffect(scene, drop, caught) {
      if (reducedMotion) return;
      const color = drop.type === 'pee' ? 0xffe36b : 0xb07b59;
      const x = caught ? drop.x : Math.max(24, Math.min(model.config.width - 24, drop.x));
      const y = caught ? model.config.catcherY - model.config.catcherHeight / 2 : model.config.height - 25;
      const count = drop.type === 'pee' ? 6 : 4;
      for (let index = 0; index < count; index++) {
        const angle = Math.PI * (0.15 + index / (count - 1) * 0.7);
        const distance = drop.type === 'pee' ? 44 : 32;
        sparkle(scene, x, y, color, drop.type === 'pee' ? 4 : 6, Math.cos(angle) * distance, -Math.sin(angle) * distance, 360);
      }
      if (caught && scene.catcherView.art) {
        scene.tweens.killTweensOf(scene.catcherView.art);
        scene.tweens.add({ targets: scene.catcherView.art, scaleX: 1.08, scaleY: 0.9, duration: 100, yoyo: true, ease: 'Back.Out' });
      }
    }

    function publish(state, force = false) {
      const signature = [state.status, state.score, state.best, state.anger, state.lastReaction].join('|');
      if (force || signature !== lastHudSignature) {
        lastHudSignature = signature;
        onUpdate(state);
      }
    }

    function render(scene, state, previous = null) {
      scene.babyView.x = state.baby.x;
      scene.babyView.y = state.baby.y;
      scene.catcherView.x = state.catcherX;
      const liveIds = new Set();
      for (const drop of state.drops) {
        liveIds.add(drop.id);
        if (!dropViews.has(drop.id)) {
          dropViews.set(drop.id, drawDrop(scene, drop));
          releaseEffect(scene, drop);
        }
        const view = dropViews.get(drop.id);
        view.x = drop.x;
        view.y = drop.y;
      }
      for (const [id, view] of dropViews) {
        if (!liveIds.has(id)) {
          const old = previous?.drops.find(drop => drop.id === id);
          if (old && state.status !== 'opening') {
            const caught = state.score > previous.score && old.y < model.config.catcherY + model.config.catcherHeight / 2 + old.radius;
            impactEffect(scene, old, caught);
          }
          view.destroy();
          dropViews.delete(id);
        }
      }
    }

    class DashScene extends Phaser.Scene {
      constructor() { super({ key: 'DiaperDash' }); }
      preload() {
        for (const name of ['baby', 'diaper', 'pee', 'poop']) if (assets[name]) this.load.image(`dash-${name}`, assets[name]);
        if (assets.babyPlayful) this.load.image('dash-baby-playful', assets.babyPlayful);
      }
      create() {
        sceneRef = this;
        this.babyView = drawBaby(this);
        this.nextExpressionAt = this.time.now + 1500 + Math.random() * 1700;
        this.expressionUntil = 0;
        this.catcherView = drawCatcher(this);
        this.keys = this.input.keyboard?.addKeys('LEFT,RIGHT,A,D');
        const movePointer = pointer => {
          const touch = pointer.wasTouch || pointer.event?.pointerType === 'touch';
          if (touch && !pointer.isDown) return;
          model.setCatcherX(pointer.x);
        };
        this.input.on('pointerdown', movePointer);
        this.input.on('pointermove', movePointer);
        render(this, model.getState());
        publish(model.getState(), true);
      }
      update(_time, delta) {
        if (model.getState().status !== 'playing') return;
        if (!reducedMotion && this.textures.exists('dash-baby-playful') && this.babyView.art) {
          if (_time >= this.nextExpressionAt) {
            this.babyView.art.setTexture('dash-baby-playful');
            this.expressionUntil = _time + 240;
            this.nextExpressionAt = _time + 1800 + Math.random() * 2600;
          } else if (this.expressionUntil && _time >= this.expressionUntil) {
            this.babyView.art.setTexture('dash-baby');
            this.expressionUntil = 0;
          }
        }
        const frameMs = Math.min(delta, 80);
        const left = this.keys?.LEFT?.isDown || this.keys?.A?.isDown;
        const right = this.keys?.RIGHT?.isDown || this.keys?.D?.isDown;
        if (left !== right && document.activeElement?.tagName !== 'INPUT') model.setCatcherX(model.getState().catcherX + (right ? 1 : -1) * model.config.keyboardSpeed * frameMs / 1000);
        const before = model.getState();
        const after = model.step(frameMs);
        render(this, after, before);
        publish(after);
        if (before.status !== 'game-over' && after.status === 'game-over') onGameOver(after);
      }
    }

    const game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: mount,
      transparent: true,
      resolution: Math.min(global.devicePixelRatio || 1, 2),
      render: { antialias: true, roundPixels: false },
      scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH, width: model.config.width, height: model.config.height },
      scene: DashScene
    });
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => game.scale?.refresh()) : null;
    resizeObserver?.observe(mount);

    return Object.freeze({
      start() {
        const state = model.start();
        if (sceneRef?.scene.isPaused()) sceneRef.scene.resume();
        if (sceneRef) {
          for (const effect of effects) effect.destroy();
          effects.clear();
          render(sceneRef, state);
        }
        publish(state, true);
        return state;
      },
      pause() {
        const state = model.pause();
        if (sceneRef?.scene.isActive()) sceneRef.scene.pause();
        publish(state);
        return state;
      },
      resume() {
        const state = model.resume();
        if (sceneRef?.scene.isPaused()) sceneRef.scene.resume();
        publish(state);
        return state;
      },
      setBest(value) {
        model.setBest(value);
        publish(model.getState(), true);
      },
      getState: () => model.getState(),
      getSettings: () => ({ width: model.config.width, height: model.config.height }),
      destroy() {
        resizeObserver?.disconnect();
        for (const effect of effects) effect.destroy();
        for (const view of dropViews.values()) view.destroy();
        effects.clear();
        dropViews.clear();
        game.destroy(true);
      }
    });
  }

  global.BBC_DIAPER_DASH_ENGINE = Object.freeze({ createEngine });
})(window);
